import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid2 as Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Tooltip,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import MultiPeriodSelector from 'components/MultiPeriodSelector'
import SinglePeriodSelector from 'components/SinglePeriodSelector'
import SimpleTitle from 'components/SimpleTitle'
import { postWithTokenWithStatus } from 'hooks/http'
import useAuth from 'hooks/useAuth'
import { usePeriods } from 'hooks/usePeriods'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import React, { FC, startTransition, useCallback, useEffect, useState } from 'react'

import { useGlobalState } from 'contexts/GlobalStateContext'
import { DATE_FORMAT, Period, createIdToPeriodFromList } from 'types/periods'
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

dayjs.extend(utc)

const RunReports: FC = () => {
  const { checkSubscriptionTierFeature } = useGlobalState()
  const { token, profile } = useAuth()
  const theme = useTheme()

  const [reportType, setReportType] = useState('')
  const [selectedOrganizations, setSelectedOrganizations] = useState<string[]>([])
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')
  const [selectedPeriods, setSelectedPeriods] = useState<string[]>([])
  const [selectedPeriodId, setSelectedPeriodId] = useState<string>('')
  const [rootPeriodId, setRootPeriodId] = useState<string>('')
  const [periods, setPeriods] = useState<Period[]>([])
  const [periodMap, setPeriodMap] = useState<{ [key: string]: Period }>({})
  const [primaryAggregation, setPrimaryAggregation] = useState('NONE')
  const [secondaryAggregation, setSecondaryAggregation] = useState('NONE')
  const [effectiveDate, setEffectiveDate] = useState<Dayjs | null>(dayjs.utc())
  const [isRunningReport, setIsRunningReport] = useState(false)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const isAccrualPeriodReport =
    reportType === 'ACCRUAL_PROGRESS' ||
    reportType === 'ACCRUAL_PROGRESS_TARGETS' ||
    reportType === 'ACCRUAL_LEDGER' ||
    reportType === 'MASTER_EMPLOYEE'
  const supportsAggregation = reportType === 'ACCRUAL_PROGRESS' || reportType === 'ACCRUAL_LEDGER'
  const requiresEffectiveDate =
    reportType === 'ACCRUAL_PROGRESS' ||
    reportType === 'ACCRUAL_PROGRESS_TARGETS' ||
    reportType === 'ACCRUAL_LEDGER' ||
    reportType === 'EMPLOYEE_ACTUALS' ||
    reportType === 'TEAM_ACTUALS'

  const { periods: fetchedPeriods, isLoadingPeriods } = usePeriods(
    token!,
    rootOrganizationId,
    !!token && selectedOrganizations.length > 0 && rootOrganizationId !== ''
  )

  useEffect(() => {
    if (fetchedPeriods) {
      setPeriods(fetchedPeriods.periods || [])
      if (fetchedPeriods.periods.length > 0) {
        setRootPeriodId(fetchedPeriods.periods[0].id!)
        setPeriodMap(createIdToPeriodFromList(fetchedPeriods.periods))
      }
    }
  }, [fetchedPeriods])

  useEffect(() => {
    if (profile && profile.roles && profile.roles.length > 0 && rootOrganizationId === '') {
      const orgId = profile.roles[0].organization.rootOrganizationId
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  useEffect(() => {
    if (selectedOrganizations.length === 0) {
      setSelectedPeriods([])
      setSelectedPeriodId('')
    }
  }, [selectedOrganizations])

  const handleReportTypeChange = (event: SelectChangeEvent) => {
    const newReportType = event.target.value as string
    setReportType(newReportType)
  }

  const handleOrganizationChange = (selected: string[]) => {
    startTransition(() => {
      setSelectedPeriods([])
      setSelectedPeriodId('')
      setSelectedOrganizations(selected)
    })
  }

  const getDescendantPeriodIds = useCallback(
    (periodId: string): string[] => {
      if (!periodId) {
        return []
      }
      const startingPeriod = periodMap[periodId]
      if (!startingPeriod) {
        return []
      }

      const ids: string[] = []
      const traverse = (period: Period | undefined) => {
        if (!period || !period.id) {
          return
        }
        ids.push(period.id)
        if (period.children && period.children.length > 0) {
          period.children.forEach(traverse)
        }
      }

      traverse(startingPeriod)
      return ids
    },
    [periodMap]
  )

  const handlePeriodChange = (selected: string[]) => {
    setSelectedPeriods(selected)
    if (selected.length > 0) {
      const rootPeriodId = periodMap[selected[0]]?.rootPeriodId
      if (rootPeriodId) {
        setRootPeriodId(rootPeriodId)
      }
    }
  }

  const handleSinglePeriodChange = (periodId: string) => {
    setSelectedPeriodId(periodId)
    const descendantIds = getDescendantPeriodIds(periodId)
    setSelectedPeriods(descendantIds)

    if (periodId) {
      const period = periodMap[periodId]
      const rootId = period?.rootPeriodId || periodId
      if (rootId) {
        setRootPeriodId(rootId)
      }
    } else {
      setRootPeriodId('')
    }
  }

  const handlePrimaryAggregationChange = (event: SelectChangeEvent) => {
    setPrimaryAggregation(event.target.value as string)
  }

  const handleSecondaryAggregationChange = (event: SelectChangeEvent) => {
    setSecondaryAggregation(event.target.value as string)
  }

  const handleSubmit = () => {
    if (isRunningReport) {
      return
    }

    setIsRunningReport(true)
    let effectiveDateValue: string | undefined
    if (effectiveDate) {
      const dateString = effectiveDate.format('YYYY-MM-DD')
      effectiveDateValue = dayjs.utc(dateString).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
    }

    const requestBody = {
      reportType,
      rootOrganizationId,
      organizationIds: selectedOrganizations,
      periodIds: selectedPeriods,
      rootPeriodId,
      ...(supportsAggregation
        ? {
            primaryAggregation,
            secondaryAggregation,
          }
        : {}),
      ...(requiresEffectiveDate && effectiveDateValue ? { effectiveDate: effectiveDateValue } : {}),
    }

    postWithTokenWithStatus('reports/download', token!, requestBody)
      .then(({ data, status }) => {
        if (status !== 200 || !data.downloadUrl) {
          throw new Error(data.error || 'Unexpected response from server')
        }

        const link = document.createElement('a')
        link.href = data.downloadUrl
        link.setAttribute('download', '')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        setSnackbar({
          open: true,
          message: 'Download started. Please check your downloads folder.',
          severity: 'success',
        })
      })
      .catch((error) => {
        console.error('Error running report:', error)
        setSnackbar({
          open: true,
          message: error.message || 'Error running report',
          severity: 'error',
        })
      })
      .finally(() => {
        setIsRunningReport(false)
      })
  }

  const handleCloseSnackbar = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbar({ ...snackbar, open: false })
  }

  const isMissingPeriodAggregation =
    supportsAggregation && primaryAggregation !== 'NONE' && secondaryAggregation === 'NONE'
  const runReportDisabled =
    !reportType ||
    selectedOrganizations.length === 0 ||
    selectedPeriods.length === 0 ||
    rootOrganizationId === '' ||
    isMissingPeriodAggregation ||
    (requiresEffectiveDate && !effectiveDate) ||
    isRunningReport

  const runReportButton = (
    <Button
      variant="contained"
      color="primary"
      onClick={handleSubmit}
      disabled={runReportDisabled}
      startIcon={isRunningReport ? <CircularProgress size={18} color="inherit" /> : undefined}
      aria-busy={isRunningReport}
    >
      {isRunningReport ? 'Running Report' : 'Run Report'}
    </Button>
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 150px)' }}>
      <SimpleTitle title="Run Reports" />
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Grid container spacing={4} sx={{ width: '50%', mt: 2, mb: 4 }} justifyContent="center">
          <Grid size={{ xs: 12 }}>
            <FormControl variant="outlined" sx={{ width: '100%' }}>
              <InputLabel shrink sx={{ backgroundColor: theme.palette.background.default }}>
                Report Type
              </InputLabel>
              <Select
                labelId="report-type-label"
                id="report-type-select"
                value={reportType}
                variant="outlined"
                label="Report Type"
                sx={{ borderRadius: '21px' }}
                onChange={handleReportTypeChange}
              >
                {checkSubscriptionTierFeature('INCENTIVE.ALL') && [
                  <MenuItem key="MASTER_EMPLOYEE" value="MASTER_EMPLOYEE">
                    Master Employee Report
                  </MenuItem>,
                  <MenuItem key="TEAM_ACTUALS" value="TEAM_ACTUALS">
                    Team Actuals Report
                  </MenuItem>,
                  <MenuItem key="EMPLOYEE_ACTUALS" value="EMPLOYEE_ACTUALS">
                    Employee Actuals Report
                  </MenuItem>,
                  <MenuItem key="ACCRUAL_PROGRESS" value="ACCRUAL_PROGRESS">
                    Progress Report
                  </MenuItem>,
                  <MenuItem key="ACCRUAL_PROGRESS_TARGETS" value="ACCRUAL_PROGRESS_TARGETS">
                    Progress Targets Report
                  </MenuItem>,
                  <MenuItem key="ACCRUAL_LEDGER" value="ACCRUAL_LEDGER">
                    Accruals Ledger Report
                  </MenuItem>,
                  <MenuItem key="EMPLOYEE_COMPENSATION_ENGAGEMENT" value="EMPLOYEE_COMPENSATION_ENGAGEMENT">
                    Employee Compensation Engagement Report
                  </MenuItem>,
                  <MenuItem key="MANAGER_COMPENSATION_ENGAGEMENT" value="MANAGER_COMPENSATION_ENGAGEMENT">
                    Manager Compensation Engagement Report
                  </MenuItem>,
                ]}
                {checkSubscriptionTierFeature('REWARDS.ALL') && [
                  <MenuItem key="EMPLOYEE_REWARD_ENGAGEMENT" value="EMPLOYEE_REWARD_ENGAGEMENT">
                    Employee Reward Engagement Report
                  </MenuItem>,
                  <MenuItem key="MANAGER_REWARD_ENGAGEMENT" value="MANAGER_REWARD_ENGAGEMENT">
                    Manager Reward Engagement Report
                  </MenuItem>,
                ]}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <MultiOrganizationSelector
              userProfile={profile!}
              selectedOrganizationIds={selectedOrganizations}
              handleChange={handleOrganizationChange}
              labelBackgroundColor={theme.palette.background.default}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            {isAccrualPeriodReport ? (
              <SinglePeriodSelector
                periods={periods}
                value={selectedPeriodId}
                onChange={handleSinglePeriodChange}
                disabled={isLoadingPeriods || selectedOrganizations.length === 0 || periods.length === 0}
                label="Selected Period"
                labelBackgroundColor={theme.palette.background.default}
                placeholder="Select a period"
                pill
              />
            ) : (
              <MultiPeriodSelector
                periods={periods}
                selectedPeriods={selectedPeriods}
                handleChange={handlePeriodChange}
                disabled={isLoadingPeriods || selectedOrganizations.length === 0 || periods.length === 0}
                label="Selected Periods"
                labelBackgroundColor={theme.palette.background.default}
                onlySingleRootPeriodSelection={true}
                autoSelectDescendants={true}
              />
            )}
          </Grid>

          {requiresEffectiveDate && (
            <Grid size={{ xs: 12 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DesktopDatePicker
                  label="Effective Date"
                  value={effectiveDate}
                  onChange={(newValue: Dayjs | null) => {
                    if (newValue) {
                      const dateString = newValue.format('YYYY-MM-DD')
                      const utcDate = dayjs.utc(dateString)
                      setEffectiveDate(utcDate)
                    } else {
                      setEffectiveDate(null)
                    }
                  }}
                  format={DATE_FORMAT}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      variant: 'outlined',
                      InputLabelProps: {
                        shrink: true,
                      },
                      InputProps: {
                        readOnly: true,
                      },
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '21px',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
          )}

          {supportsAggregation && (
            <>
              <Grid size={{ xs: 12 }}>
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel shrink sx={{ backgroundColor: theme.palette.background.default }}>
                    Primary Aggregation
                  </InputLabel>
                  <Select
                    labelId="primary-aggregation-label"
                    id="primary-aggregation-select"
                    value={primaryAggregation}
                    variant="outlined"
                    label="Primary Aggregation"
                    sx={{ borderRadius: '21px' }}
                    onChange={handlePrimaryAggregationChange}
                    defaultValue="NONE"
                  >
                    <MenuItem value="NONE">None</MenuItem>
                    <MenuItem value="LEDGER_ACCOUNT">Ledger Account</MenuItem>
                    <MenuItem value="TEAM">Team</MenuItem>
                    <MenuItem value="EMPLOYEE">Employee</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <FormControl variant="outlined" sx={{ width: '100%' }}>
                  <InputLabel shrink sx={{ backgroundColor: theme.palette.background.default }}>
                    Period Aggregation
                  </InputLabel>
                  <Select
                    labelId="period-aggregation-label"
                    id="period-aggregation-select"
                    value={secondaryAggregation}
                    variant="outlined"
                    label="Period Aggregation"
                    sx={{ borderRadius: '21px' }}
                    onChange={handleSecondaryAggregationChange}
                    defaultValue="NONE"
                  >
                    <MenuItem value="NONE">None</MenuItem>
                    <MenuItem value="FISCAL_YEAR">Fiscal Period</MenuItem>
                    <MenuItem value="SUB_PERIOD">Fiscal Sub-Period</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            {isMissingPeriodAggregation ? (
              <Tooltip title="Select a period aggregation when using a primary aggregation">
                <span>{runReportButton}</span>
              </Tooltip>
            ) : (
              runReportButton
            )}
          </Grid>
        </Grid>
      </Box>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default RunReports
